import { zodResolver } from '@hookform/resolvers/zod';
import {
    ArrayInput,
    AutocompleteInput,
    ButtonProps,
    NumberInput,
    ReferenceInput,
    SimpleFormIterator,
    useCreatePath,
    useDataProvider,
    useGetIdentity,
    useGetList,
    useGetMany,
    useGetOne,
    useInput,
    usePermissions,
    useSimpleFormIterator,
    useSimpleFormIteratorItem
} from 'react-admin';
import { Button, IconButton, Unstable_Grid2 as Grid } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { Add, DeleteTwoTone } from '@mui/icons-material';
import { z } from 'zod';
import { useFormContext, useWatch } from 'react-hook-form';

import { FormSection, SimpleForm } from '@football-kids/client-shared';
import { MODULE_RESOURCE } from '../../type';
import { AccountRole } from '@football-kids/shared/enums';
import type { Account } from '@football-kids/shared/entity/Account';
import type { Child } from '@football-kids/shared/entity/Child';
import type { Agreement } from '@football-kids/shared/entity/Agreement';
import { AppPermissions } from '@football-kids/client-lib/auth-provider';
import { CustomerInventoryItem } from '../../medusa-types';
import { useLocation } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';


export const MyAddButton = (props: ButtonProps) => {
    const { add } = useSimpleFormIterator();
    return (
        <Button size={'small'} startIcon={<Add />} onClick={() => add()} {...props}>
            Dodaj przedmiot
        </Button>
    );
};

export const MyRemoveButton = (props: Omit<ButtonProps, 'onClick'>) => {
    const { remove } = useSimpleFormIteratorItem();
    return (
        <IconButton onClick={() => remove()}>
            <DeleteTwoTone />
        </IconButton>
    );
};

export const AgreementInput = ({ source, parentSource }: { source: string; parentSource: string }) => {
    const parentId = useWatch({ name: parentSource });
    const { field: { onChange } } = useInput({ source });
    const previousParentId = useRef(parentId);

    useEffect(() => {
        if (previousParentId.current !== parentId) {
            previousParentId.current = parentId;
            onChange(null);
        }
    }, [parentId, onChange]);

    const { data: parent, isLoading: isParentLoading, error: parentError } = useGetOne<Account>(
        'accounts',
        { id: parentId },
        { enabled: !!parentId }
    );
    const childrenIds = parent?.childrenIds ?? [];
    const { data: children, isLoading: areChildrenLoading, error: childrenError } = useGetMany<Child>(
        'children',
        { ids: childrenIds },
        { enabled: !!parentId && childrenIds.length > 0 }
    );
    const agreementsIds = useMemo(
        () => [...new Set(children?.flatMap(child => child.agreementsIds ?? []) ?? [])],
        [children]
    );
    const { data: agreements = [], isLoading: areAgreementsLoading, error: agreementsError } = useGetMany<Agreement>(
        'agreements',
        { ids: agreementsIds },
        { enabled: !!parentId && agreementsIds.length > 0 }
    );
    const isLoading = isParentLoading || areChildrenLoading || areAgreementsLoading;
    const hasError = !!(parentError || childrenError || agreementsError);

    return (
        <AutocompleteInput
            source={source}
            label="Umowa"
            choices={parentId && !hasError ? agreements : []}
            optionText={(agreement: Agreement) => [
                agreement.agreementNumber || agreement.id,
                [agreement.childName, agreement.childSurname].filter(Boolean).join(' ')
            ].filter(Boolean).join(' — ')}
            optionValue="id"
            isLoading={isLoading}
            disabled={!parentId || isLoading || hasError || agreements.length === 0}
            helperText={hasError
                ? 'Nie udało się pobrać umów rodzica'
                : !parentId
                    ? 'Najpierw wybierz rodzica'
                    : !isLoading && agreements.length === 0
                        ? 'Brak umów dla dzieci wybranego rodzica'
                        : undefined}
        />
    );
};

const DynamicProductInput = ({ source, ...props }: { source: string, label: string }) => {
    const { data: inventories = [], isLoading: isInventoryLoading } = useGetList<CustomerInventoryItem>(
        MODULE_RESOURCE.CustomerInventories,
        { pagination: { page: 1, perPage: 1000 } }
    );

    const productChoices = useMemo(() => {
        const map = new Map();
        inventories.forEach((inv: any) => {
            if (inv.variant.product && !map.has(inv.variant.product.id)) {
                map.set(inv.variant.product.id, inv.variant.product);
            }
        });
        return Array.from(map.values());
    }, [inventories]);

    return <AutocompleteInput
        source={source}
        choices={productChoices}
        isLoading={isInventoryLoading}
        optionText="title"
        optionValue="id"
        {...props}
    />;
};

const DynamicVariantInput = ({ source, inventories, ...props }: any) => {
    const { watch } = useFormContext();
    const { field: { name } } = useInput({ source });

    const productIdPath = name.replace('variantId', 'productId');
    const currentProductId = watch(productIdPath);

    const variantChoices = useMemo(() => {
        if (!currentProductId || !inventories) return [];
        return inventories
            .filter((inv: any) => inv.variant.product.id === currentProductId && inv.variant)
            .map((inv: any) => ({
                id: inv.variant.id,
                title: `${inv.variant.title} (Dostępne: ${inv.stock.stockedQuantity})`
            }));
    }, [currentProductId, inventories]);

    return (
        <AutocompleteInput
            source={source}
            choices={variantChoices}
            optionText="title"
            optionValue="id"
            label="Wariant"
            disabled={!currentProductId}
            sx={{ minWidth: 250 }}
            {...props}
        />
    );
};


const DynamicQuantityInput = ({ source, inventories, ...props }: any) => {
    const { watch } = useFormContext();
    const { field: { name } } = useInput({ source });

    const productIdPath = name.replace('quantity', 'productId');
    const variantIdPath = name.replace('quantity', 'variantId');
    const currentProductId = watch(productIdPath);
    const currentVariantId = watch(variantIdPath);

    const inventoryItem = inventories.find(
        (inv: any) => inv.variant.id === currentVariantId && inv.variant.product.id === currentProductId
    );

    const maxQty = inventoryItem?.stock?.stockedQuantity || 1;

    return (
        <NumberInput
            source={source}
            defaultValue={1}
            max={maxQty}
            min={1}
            label={`resources.${MODULE_RESOURCE.Products}.fields.quantity`}
            helperText={`Maks.: ${maxQty}`}
            {...props}
        />
    );
};

const zodDebugResolver = (schema: any) => async (data: any, context: any, options: any) => {
    console.log('formData', data);
    console.log('validation result', await zodResolver(schema)(data, context, options));
    return zodResolver(schema)(data, context, options);
};

const customerInventoryTransferSchema = z.object({
    accountId: z.string().min(1, 'Wybierz klienta'),
    parentId: z.string().min(1, 'Wybierz rodzica'),
    agreementId: z.string().nullish(),
    items: z.object({
        productId: z.string().min(1, 'Wybierz produkt'),
        variantId: z.string().min(1, 'Wybierz wariant'),
        quantity: z.number().min(1, 'Ilość musi być większa niż 0')
    })
        .array()
        .min(1, 'Dodaj przynajmniej jeden przedmiot')
        .superRefine((items, ctx) => {
            const usedVariantIds = new Set<string>();

            items.forEach((item, index) => {
                if (usedVariantIds.has(item.variantId)) {

                    ctx.addIssue({
                        code: "custom",
                        message: 'Warianty nie mogą się powtarzać',
                        path: [index, 'variantId']
                    });
                } else {
                    usedVariantIds.add(item.variantId);
                }
            });
        })
});

const getDefaultTransfer = () => ({
    accountId: null,
    parentId: null,
    agreementId: null,
    items: [{ productId: null, variantId: null, quantity: 1 }]
});

const CustomerInventoryTransferForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const createPath = useCreatePath();

    const { permissions } = usePermissions<AppPermissions>();
    const { data: identityData, isLoading } = useGetIdentity();
    const ownSchoolIds = permissions?.[AccountRole.ADMIN]
        ? null
        : identityData?.['account']?.schoolsIds || [];

    const { data: ownGroups = [], isSuccess: groupsReady } = useGetList(
        'groups',
        { filter: { schoolsIds: ownSchoolIds }, pagination: { page: 1, perPage: 1000 } },
        { enabled: !!permissions }
    );

    const ownCoachIds: any[] = [...new Set(ownGroups.flatMap(x => x.coachesIds))];

    const { data: inventories = [] } = useGetList(
        MODULE_RESOURCE.CustomerInventories,
        { pagination: { page: 1, perPage: 1000 } }
    );

    const dataProvider = useDataProvider();
    const mutation = useMutation({
        onSuccess: (...args) => {
            // void notify('Wyzwania zostały dodane');
        },
        mutationFn: async (value: any) => {
            console.log('GroupFormAchievements');
            console.log(value);

            await dataProvider['transferInventory'](value);
            navigate(createPath({
                resource: MODULE_RESOURCE.CustomerInventories,
                type: 'list'
            }));
        }

    });


    return (
        <SimpleForm
            resolver={zodDebugResolver(customerInventoryTransferSchema)}
            defaultValues={{ ...getDefaultTransfer(), ...location.state?.defaultValues }}
            onSubmit={mutation.mutateAsync}
        >
            <FormSection label={'Dane trenera'}>
                <Grid xs={12} md={8}>
                    <ReferenceInput
                        source={'accountId'}
                        reference={'accounts'}
                        filter={{ roles: [AccountRole.COACH], ids: ownCoachIds }}
                        queryOptions={{ enabled: groupsReady }}
                    />
                </Grid>
            </FormSection>

            <FormSection label={'Rodzic'}>
                <Grid xs={12} md={8}>
                    <ReferenceInput
                        source={'parentId'}
                        reference={'accounts'}
                        filter={{ roles: [AccountRole.PARENT] }}
                    />
                </Grid>

                <Grid xs={12} md={8}>
                    <AgreementInput source="agreementId" parentSource="parentId" />
                </Grid>
            </FormSection>

            <FormSection label={'Przedmioty do przekazania'}>
                <Grid xs={12}>
                    <ArrayInput
                        source="items"
                        label={false}
                        sx={{
                            '.RaSimpleFormIterator-line': {
                                alignItems: 'center',
                                flexDirection: 'row',
                                mb: 2,
                                '.RaSimpleFormIterator-inline': {
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                    flexWrap: 'nowrap',
                                    flexGrow: 1,
                                    gap: 2
                                },
                                '.RaSimpleFormIterator-action': {
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                    flexWrap: 'nowrap',
                                    visibility: 'visible'
                                }
                            }
                        }}
                    >
                        <SimpleFormIterator
                            inline
                            disableReordering
                            disableClear
                            addButton={<MyAddButton />}
                            removeButton={<MyRemoveButton />}
                        >
                            <DynamicProductInput
                                source={'productId'}
                                label={`resources.${MODULE_RESOURCE.Products}.fields.product`}
                            />

                            <DynamicVariantInput
                                source="variantId"
                                inventories={inventories}
                            />

                            <DynamicQuantityInput
                                source="quantity"
                                inventories={inventories}
                            />
                        </SimpleFormIterator>
                    </ArrayInput>
                </Grid>
            </FormSection>
        </SimpleForm>
    );
};

export default CustomerInventoryTransferForm;
